import { Platform } from "react-native";
import { create } from "zustand";
import { persist, createJSONStorage, type StateStorage } from "zustand/middleware";
import AsyncStorage from "@react-native-async-storage/async-storage";

interface SidebarState {
  collapsed: boolean;
  expandedItemIds: readonly string[];
  setCollapsed: (collapsed: boolean) => void;
  toggleCollapsed: () => void;
  setExpandedItemIds: (ids: readonly string[]) => void;
}

const webLocalStorage: StateStorage = {
  getItem: (name) => {
    try {
      return window.localStorage.getItem(name);
    } catch {
      return null;
    }
  },
  setItem: (name, value) => {
    try {
      window.localStorage.setItem(name, value);
    } catch {
      // localStorage may be unavailable
    }
  },
  removeItem: (name) => {
    try {
      window.localStorage.removeItem(name);
    } catch {
      // localStorage may be unavailable
    }
  },
};

export const useSidebarStore = create<SidebarState>()(
  persist(
    (set) => ({
      collapsed: false,
      expandedItemIds: [],
      setCollapsed: (collapsed) => set({ collapsed }),
      toggleCollapsed: () => set((state) => ({ collapsed: !state.collapsed })),
      setExpandedItemIds: (ids) => set({ expandedItemIds: ids }),
    }),
    {
      name: "@web_sidebar",
      storage: createJSONStorage(() => (Platform.OS === "web" ? webLocalStorage : AsyncStorage)),
      merge: (persisted, current) => {
        const stored = persisted as Partial<SidebarState> | null;
        return {
          ...current,
          collapsed: typeof stored?.collapsed === "boolean" ? stored.collapsed : current.collapsed,
          expandedItemIds:
            (
              Array.isArray(stored?.expandedItemIds) &&
              stored.expandedItemIds.every((v: unknown) => typeof v === "string")
            ) ?
              stored.expandedItemIds
            : current.expandedItemIds,
        };
      },
    },
  ),
);
